import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { Session } from '@hub/hooks/states/useSessionStore';
import type { IbhDimensions } from '@hub/bearing-hub/common/dimensions/dimensions';
import { fetchSimilarParts } from '@hub/bearing-hub/BearingHubPage/parts/fetchSimilarParts';
import type {
  IbhPartsResponseDto,
  IbhProductionMethods,
  IbhShape,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import type { NextRuntimeConfiguration } from '@hub/libs/config';
import getConfig from 'next/config';
import type { UnitSystem } from '@igus/kopla-domain';

const { publicRuntimeConfig } = getConfig() as NextRuntimeConfiguration;

type UseBearingSimilarPartsApi = {
  shape: IbhShape;
  materials: string[];
  productionMethods: IbhProductionMethods[];
  enabled: boolean;
  unitSystem: UnitSystem;
  quantity: number;
  dimensions?: IbhDimensions;
  session?: Session;
};

export const useBearingSimilarPartsApi = ({
  shape,
  materials,
  productionMethods,
  enabled,
  unitSystem,
  quantity,
  dimensions,
  session,
}: UseBearingSimilarPartsApi) => {
  const queryKey = [
    'bearing-similar-parts',
    session?.currency,
    quantity,
    unitSystem,
    shape,
    JSON.stringify(dimensions),
    materials.join(','),
    productionMethods?.join(','),
  ].filter(Boolean);

  const fetchSimilarPartsData = () =>
    fetchSimilarParts(
      { sid: session.sessionId },
      {
        shape,
        unitSystem,
        quantity,
        productionMethods,
        materials,
        dimensions,
      }
    );

  const { data, isFetching, isLoading, status } = useQuery({
    queryKey,
    queryFn: fetchSimilarPartsData,
    enabled,
    gcTime: publicRuntimeConfig.ibhUseQueryCacheTtl,
    retry: failureCount => failureCount <= 1 && navigator.onLine,
  }) as UseQueryResult<IbhPartsResponseDto[]>;

  return { data, isFetching, isLoading, status };
};
